import React, { useEffect, useState } from 'react';
import { Box, useDisclosure, useStyleConfig } from '@chakra-ui/react';
import { SkipNavContent, SkipNavLink } from '@chakra-ui/skip-nav';
import { StorefrontPreview } from '@salesforce/commerce-sdk-react/components';
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { IntlProvider } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useAddPreviewParamIfNeeded } from '../../bff-api/customers/useAddPreviewParamIfNeeded';
import { useCustomerGet } from '../../bff-api/customers/useCustomerGet';
import { useSSRPreviewMode } from '../../bff-api/customers/useSSRPreviewMode';
import { DEFAULT_LOCALE, DEFAULT_SITE_TITLE, THEME_COLOR } from '../../constants';
import { ProductsAddedToBagDrawerProvider } from '../../contexts/products-added-to-bag-drawer';
import { CustomerProvider } from '../../contexts/useCustomer';
import { useGTM, useMultiSite } from '../../hooks';
import { useActOnPreviewContextChange } from '../../hooks/useActOnPreviewContextChange';
import { useManualScrollRestorationOrAuto } from '../../hooks/useManualScrollRestorationOrAuto';
import { PageUrl } from '../../pages/utils/page-url';
import { SLTConfigProvider, useSLTConfig } from '../../slt/config/SLTConfigProvider';
import { useSLTAppConfigs } from '../../slt/config/useConfigs';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { isNoIndexPage } from '../../utils/is-no-index-page';
import { fetchTranslations, getTargetLocale } from '../../utils/locale';
import { sfccUrl } from '../../utils/sitegenesis';
import { getOriginUrl } from '../../utils/url-utils';
import { isServer, watchOnlineStatus } from '../../utils/utils';
import OfflineBanner from '../offline-banner';
import OfflineBoundary from '../offline-boundary';
import Seo from '../seo';
import { AppHead } from './AppHead';

const SLTApp = ({ children }) => {
    const isSSRPreviewMode = useSSRPreviewMode();
    useAddPreviewParamIfNeeded();
    const authContext = useAuthContext();
    useActOnPreviewContextChange();
    useEffect(() => {
        authContext.ready().finally(() => {
            if (!isServer) {
                window.completePwaAuthFlow();
            }
        });
    }, [authContext]);
    const getTokenWhenReady = async () => {
        if (!isServer) await window.sfccSessionCreationPromise;
        return authContext.ready().then((data) => data.access_token);
    };
    const location = useLocation();
    const { init } = useGTM();

    const { data: customer } = useCustomerGet();

    const appOrigin = getOriginUrl();

    const shouldNoIndex = isNoIndexPage(location?.pathname);

    const { site, locale, buildUrl } = useMultiSite();

    const [isOnline, setIsOnline] = useState(true);
    const styles = useStyleConfig('App');

    const { onClose } = useDisclosure();

    const { customPreferences } = useSLTConfig();

    init();

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((online) => {
            setIsOnline(online);
        });

        window.rewardsMessaging = customPreferences[CustomPreference.REWARDS_MESSAGING];
        window.savingsFeatureMode = customPreferences[CustomPreference.SAVINGS_FEATURE_ENABLED];
    }, []);

    useEffect(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose();
    }, [location]);

    useEffect(() => {
        init();
    }, [init, location]);

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname,
    );

    const gtmId = customPreferences[CustomPreference.GOOGLE_TAG_MANAGER_ID];
    const applePayScanToPayEnabled =
        customPreferences[CustomPreference.APPLE_PAY_SCAN_TO_PAY_ENABLED];

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () =>
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            [locale?.id || DEFAULT_LOCALE],
        l10nConfig: site.l10n,
    });

    const { data: messages } = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {};
            }
            return fetchTranslations(targetLocale);
        },
        enabled: isServer,
    });

    return (
        <CustomerProvider>
            <ProductsAddedToBagDrawerProvider>
                <Box className="sf-app" {...styles.container}>
                    <Helmet>
                        <script type="text/javascript">
                            {`
                            /* If browser back button was used, flush cache */
                            (function () {
                                if(!window.localStorage.getItem('is-refresh')) {
                                  window.onpageshow = event => {
                                    if (event.persisted) {
                                      window.localStorage.setItem('is-refresh', true);
                                      window.location.reload();
                                    }
                                  }
                                } else {
                                  window.localStorage.removeItem('is-refresh');
                                }
                            })();
                        `}
                        </script>

                        {/**
                         * This is a script to make sure the sessions (SG / PWA) are in sync
                         * Without this, the PWA application will not be able to see the auth token in the cookies
                         * and will try to generate a new one based on the refresh token every time.
                         */}
                        <script>
                            {`
                            (function(w){
                                var isLocationOnMobifyHost = w.location?.hostname?.endsWith('.mobify-storefront.com');
                                var prefix = isLocationOnMobifyHost ? '/mobify/proxy/ocapi' : '';
                                w.completePwaAuthFlow = null;
                                w.pwaAuthFlowPromise = new Promise((res, _) => {
                                    w.completePwaAuthFlow = res;
                                });
                                w.sfccSessionCreationPromise =
                                    fetch(prefix + '${sfccUrl('PwaUtils-EnsureSiteGenesisSession', [
                                        { t: '' },
                                    ])}'+Date.now())
                                        .then((r)=>{if (!r.ok){throw new Error('HTTP Error calling PwaUtils-EnsureSiteGenesisSession');}return r;})
                                        .then((r) => r.text())
                                        .catch((e) => {console.warn('There was an error creating the SG session', e);});
                            })(window);
                        `}
                        </script>
                        <script type="text/javascript">
                            {`
                            (function(){
                                /* This is to avoid Safari from sending basic Auth */
                                /* https://stackoverflow.com/questions/36789514/how-to-prevent-override-authorization-header-in-safari */
                                var savedFetch = window.fetch;
                                window.fetch = function() {
                                    var args = [].slice.call(arguments, 0);
                                    if (args.length > 0 && /.*\\/commerce-api\\/.*/.test(args[0])) {
                                        var options = Object.assign({}, args[1] || {});
                                        options.headers = Object.assign({}, options.headers || {});
                                        options.headers['x-slt-rewriteauth'] = options.headers.Authorization || options.headers.authorization || '';
                                        args[1] = options;
                                    }
                                    return savedFetch.apply(this, args);
                                }
                            })();
                        `}
                        </script>
                        {applePayScanToPayEnabled ? (
                            <script src="https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js" />
                        ) : null}
                    </Helmet>
                    <AppHead />
                    {/* Google Tag Manager */}
                    {gtmId && customer && !isSSRPreviewMode ? (
                        <noscript>
                            <iframe
                                title="GTM iframe"
                                src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
                                height="0"
                                width="0"
                                style={{ display: 'none', visibility: 'hidden' }}
                            />
                        </noscript>
                    ) : null}

                    <StorefrontPreview getToken={getTokenWhenReady}>
                        <IntlProvider
                            onError={(err) => {
                                if (err.code === 'MISSING_TRANSLATION') {
                                    // NOTE: Remove the console error for missing translations during development,
                                    // as we knew translations would be added later.
                                    console.warn('Missing translation', err.message);
                                    return;
                                }
                                throw err;
                            }}
                            locale={targetLocale}
                            messages={messages}
                            // For react-intl, the _default locale_ refers to the locale that the inline
                            // `defaultMessage`s are written for.
                            // NOTE: if you update this value, please also update the following npm scripts in
                            // `template-retail-react-app/package.json`:
                            // - "extract-default-translations"
                            // - "compile-translations:pseudo"
                            defaultLocale={DEFAULT_LOCALE}
                        >
                            <Seo
                                title={DEFAULT_SITE_TITLE}
                                description="Shop Sur La Table for the finest cookware, dinnerware, cutlery, kitchen electrics, bakeware and more. Our cooking class program is one of the largest in the nation."
                                noIndex={shouldNoIndex}
                            >
                                <meta name="theme-color" content={THEME_COLOR} />
                                <meta
                                    name="apple-mobile-web-app-title"
                                    content={DEFAULT_SITE_TITLE}
                                />
                                <link
                                    rel="apple-touch-icon"
                                    href={getAssetUrl('static/img/global/apple-touch-icon.png')}
                                />
                                <link rel="manifest" href={getAssetUrl('static/manifest.json')} />
                                {/* Urls for all localized versions of this page (including current page)
                            For more details on hrefLang, see https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
                                {site.l10n?.supportedLocales.map((supportedLocale) => (
                                    <link
                                        rel="alternate"
                                        hrefLang={supportedLocale.id.toLowerCase()}
                                        href={`${appOrigin}${buildUrl(location.pathname)}`}
                                        key={supportedLocale.id}
                                    />
                                ))}
                                {/* A general locale as fallback. For example: "en" if default locale is "en-GB" */}
                                <link
                                    rel="alternate"
                                    hrefLang={site.l10n.defaultLocale.slice(0, 2)}
                                    href={`${appOrigin}${buildUrl(location.pathname)}`}
                                />
                                {/* A wider fallback for user locales that the app does not support */}
                                <link rel="alternate" hrefLang="x-default" href={`${appOrigin}/`} />
                            </Seo>

                            <div id="app" className="flex flex-1 flex-col">
                                <SkipNavLink zIndex="skipLink">Skip to Content</SkipNavLink>
                                {!isOnline && <OfflineBanner />}
                                <SkipNavContent
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1,
                                        outline: 0,
                                    }}
                                >
                                    <Box
                                        as="main"
                                        id="app-main"
                                        role="main"
                                        display="flex"
                                        flexDirection="column"
                                        flex="1"
                                    >
                                        <OfflineBoundary isOnline={false}>
                                            {children}
                                        </OfflineBoundary>
                                    </Box>
                                </SkipNavContent>
                            </div>
                        </IntlProvider>
                    </StorefrontPreview>
                </Box>
            </ProductsAddedToBagDrawerProvider>
        </CustomerProvider>
    );
};

const App = (props) => {
    // The following must run as early as possible so react-ssr-prepass captures this as the first useQuery in the cache
    const sltServerConfig = useSLTAppConfigs();
    useManualScrollRestorationOrAuto({
        paths: [
            PageUrl.PWA_PRODUCTS,
            PageUrl.PWA_SALE,
            PageUrl.PWA_NEW,
            PageUrl.PRODUCTS,
            PageUrl.SALE,
            PageUrl.NEW,
        ],
    });

    return (
        <SLTConfigProvider data={sltServerConfig}>
            <SLTApp {...props} />
        </SLTConfigProvider>
    );
};

App.propTypes = {
    children: PropTypes.node,
};

App.defaultProps = {
    children: null,
};

export default App;
